import React, { useRef, useState } from "react";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";
import PortalDropdown from "../../components/PortalDropdown";

const CAST_TOGGLEABLE = { pickup_time: "Pickup", arrive_time: "Arrive", hmua_call: "H/MU", notes: "Notes" };
const CAST_HEADERS = [
  { key: "_num", label: "#" },
  { key: "character", label: "Character" },
  { key: "actor", label: "Talent" },
  { key: "status", label: "S/W/F" },
  { key: "pickup_time", label: "Pickup" },
  { key: "arrive_time", label: "Arrive" },
  { key: "hmua_call", label: "H/MU" },
  { key: "onset_call", label: "On Set" },
  { key: "notes", label: "Notes" },
  { key: "_del", label: "" },
];

export default function CastCallTimesSection({ form, setForm, crew, inputCls, selectCls, Section }) {
  const addCast = () => setForm(f => ({ ...f, cast_calls: [...f.cast_calls, { cast_id: "", character: "", actor: "", status: "W", pickup_time: "", arrive_time: "", hmua_call: "", onset_call: "", notes: "", crew_id: "", phone: "", email: "" }] }));
  const updCast = (i, k, v) => setForm(f => ({ ...f, cast_calls: f.cast_calls.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const delCast = (i) => setForm(f => ({ ...f, cast_calls: f.cast_calls.filter((_, idx) => idx !== i) }));

  return (
    <Section title="Cast Call Times" defaultOpen={(form.cast_calls || []).length > 0}>
      <div className="pt-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2 pb-2 text-[11px]">
          <span className="text-gray-500 uppercase tracking-widest font-mono mr-1">On call sheet:</span>
          <button type="button" data-testid="cast-show-email-toggle"
            onClick={() => setForm(f => ({ ...f, show_cast_email: !f.show_cast_email }))}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all ${form.show_cast_email ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-[#0D0F12] text-gray-400 border-[#28303F] hover:border-gray-500"}`}>
            {form.show_cast_email ? <Eye size={11}/> : <EyeOff size={11}/>} Cast emails
          </button>
          <button type="button" data-testid="cast-show-phone-toggle"
            onClick={() => setForm(f => ({ ...f, show_cast_phone: !f.show_cast_phone }))}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all ${form.show_cast_phone ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-[#0D0F12] text-gray-400 border-[#28303F] hover:border-gray-500"}`}>
            {form.show_cast_phone ? <Eye size={11}/> : <EyeOff size={11}/>} Cast phone numbers
          </button>
        </div>
        {form.cast_calls.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#28303F]">
                  {CAST_HEADERS.map(({ key, label }) => {
                    const toggle = CAST_TOGGLEABLE[key];
                    const hid = (form.hidden_cast_columns || []).includes(key);
                    if (!toggle) return <th key={key} className="text-left text-gray-500 pb-2 pr-2 font-medium">{label}</th>;
                    return (
                      <th key={key} className={`text-left text-gray-500 pb-2 pr-2 font-medium ${hid ? "opacity-40" : ""}`}>
                        <span className="inline-flex items-center gap-1">
                          {label}
                          <button type="button" data-testid={`cast-col-toggle-${key}`}
                            onClick={() => setForm(f => {
                              const cur = f.hidden_cast_columns || [];
                              return { ...f, hidden_cast_columns: cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key] };
                            })}
                            title={hid ? "Show column on call sheet" : "Hide column from call sheet"}
                            className={`transition-opacity ${hid ? "text-gray-500 hover:text-amber-400" : "opacity-40 hover:opacity-100 hover:text-amber-400"}`}>
                            {hid ? <EyeOff size={10} /> : <Eye size={10} />}
                          </button>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {form.cast_calls.map((c, i) => (
                  <CastRow key={i} i={i} c={c} crew={crew} form={form}
                    inputCls={inputCls} selectCls={selectCls}
                    updCast={updCast} delCast={delCast} setForm={setForm} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <button data-testid="builder-add-cast-btn" onClick={addCast}
          className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm mt-2 transition-colors">
          <Plus size={15}/> Add Cast Member
        </button>
      </div>
    </Section>
  );
}

function CastRow({ i, c, crew, form, inputCls, selectCls, updCast, delCast, setForm }) {
  const actorWrapRef = useRef(null);
  const [actorFocused, setActorFocused] = useState(false);
  const talent = (crew || []).filter(p => (p.department || "").toLowerCase() === "talent");
  const query = (c.actor || "").trim().toLowerCase();
  const matches = query ? talent.filter(p => (p.name || "").toLowerCase().includes(query) && (p.name || "").toLowerCase() !== query).slice(0, 6) : [];
  const locked = !!c.crew_id;
  const hiddenCast = form.hidden_cast_columns || [];
  const colCls = (k) => hiddenCast.includes(k) ? "opacity-40 pointer-events-none" : "";
  const pickTalent = (p) => {
    setForm(f => ({
      ...f,
      cast_calls: f.cast_calls.map((row, idx) => idx === i ? {
        ...row, crew_id: p.id, actor: p.name || "",
        character: p.role || p.position || row.character || "",
        phone: p.phone || "", email: p.email || "",
      } : row),
    }));
    setActorFocused(false);
  };
  const clearTalent = () => setForm(f => ({
    ...f,
    cast_calls: f.cast_calls.map((row, idx) => idx === i ? { ...row, crew_id: "", actor: "", character: "", phone: "", email: "" } : row),
  }));
  const lockedInputCls = `${inputCls} bg-[#141922] cursor-not-allowed text-amber-300/90 border-amber-500/20`;

  return (
    <tr className="border-b border-[#28303F]/50">
      <td className="py-1 pr-2 w-10"><input value={c.cast_id} onChange={e=>updCast(i,"cast_id",e.target.value)} className={inputCls} placeholder="1" /></td>
      <td className="py-1 pr-2">
        <input value={c.character} onChange={e=>updCast(i,"character",e.target.value)}
          className={locked ? lockedInputCls : inputCls}
          data-testid={`cast-character-input-${i}`} readOnly={locked}
          title={locked ? "Locked — auto-filled from People Database (Talent). Clear the Actor to edit." : ""} />
      </td>
      <td className="py-1 pr-2">
        {locked ? (
          <div data-testid={`cast-actor-locked-${i}`}
            className={`${lockedInputCls} flex items-center justify-between gap-2`}
            title="Locked — pulled from People Database. Click ✕ to change.">
            <span className="truncate font-medium">{c.actor}</span>
            <button type="button" data-testid={`cast-actor-clear-${i}`} onClick={clearTalent}
              className="text-amber-400/60 hover:text-red-400 text-xs px-1 shrink-0" title="Clear talent selection">✕</button>
          </div>
        ) : (
          <div ref={actorWrapRef} className="relative">
            <input value={c.actor} onChange={e=>updCast(i,"actor",e.target.value)}
              className={inputCls} data-testid={`cast-actor-input-${i}`} autoComplete="off"
              onFocus={() => setActorFocused(true)}
              onBlur={() => setTimeout(() => setActorFocused(false), 120)}
              placeholder={talent.length === 0 ? "Add Talent to People DB…" : "Type to search Talent…"} />
            <PortalDropdown
              anchorRef={actorWrapRef}
              open={actorFocused && matches.length > 0}
              maxHeight={240}
              className="bg-[#141922] border border-amber-500/30 rounded-lg shadow-xl overflow-hidden"
            >
              <div data-testid={`cast-actor-suggestions-${i}`}>
                {matches.map((p, k) => (
                  <button key={p.id || k} type="button"
                    data-testid={`cast-actor-suggestion-${i}-${k}`}
                    onMouseDown={(e) => { e.preventDefault(); pickTalent(p); }}
                    className="w-full flex items-center justify-between gap-2 text-left px-3 py-1.5 text-xs text-gray-200 hover:bg-amber-500/10 hover:text-white">
                    <span className="font-medium">{p.name}</span>
                    <span className="text-[10px] text-gray-500">
                      {(p.role || p.position) ? (p.role || p.position) : "—"}
                      {p.phone && <span className="text-gray-600"> · {p.phone}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </PortalDropdown>
          </div>
        )}
      </td>
      <td className="py-1 pr-2 w-14"><select value={c.status} onChange={e=>updCast(i,"status",e.target.value)} className={selectCls}><option>W</option><option>SW</option><option>SWF</option><option>H</option><option>WF</option></select></td>
      <td className={`py-1 pr-2 w-20 ${colCls("pickup_time")}`}><input value={c.pickup_time} onChange={e=>updCast(i,"pickup_time",e.target.value)} className={inputCls} placeholder="06:00" /></td>
      <td className={`py-1 pr-2 w-20 ${colCls("arrive_time")}`}><input value={c.arrive_time || ""} onChange={e=>updCast(i,"arrive_time",e.target.value)} className={inputCls} placeholder="06:15" data-testid={`cast-arrive-input-${i}`} /></td>
      <td className={`py-1 pr-2 w-20 ${colCls("hmua_call")}`}><input value={c.hmua_call} onChange={e=>updCast(i,"hmua_call",e.target.value)} className={inputCls} placeholder="06:30" /></td>
      <td className="py-1 pr-2 w-20"><input value={c.onset_call} onChange={e=>updCast(i,"onset_call",e.target.value)} className={inputCls} placeholder="07:00" /></td>
      <td className={`py-1 pr-2 ${colCls("notes")}`}><input value={c.notes} onChange={e=>updCast(i,"notes",e.target.value)} className={inputCls} /></td>
      <td className="py-1"><button onClick={()=>delCast(i)} className="text-gray-600 hover:text-red-400 p-1"><Trash2 size={13}/></button></td>
    </tr>
  );
}
